import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box, RadioGroup } from "@mui/material";
import { MyTimeTimePeriodOption } from "my-time/components";
import { OcCard, OcCardButtonBar, OcConfirmationDialog } from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcProperty, useOcSnackbar } from "oc/hooks";

type MyTimeTimePeriodSettingsCardProps = {
  isMobile: boolean;
};

export const MyTimeTimePeriodSettingsCard = ({
  isMobile,
}: MyTimeTimePeriodSettingsCardProps) => {
  const { t } = useTranslation();

  const { setOpenMessage } = useOcSnackbar();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [timePeriod, setTimePeriod] = useState(0);
  const [disabled, setDisabled] = useState(true);

  const { data } = useOcProperty({
    resource: "myTime",
    key: "timePeriod",
    onCompleted: ({ value }) => {
      const newTimePeriod = Number.parseInt(value);
      setTimePeriod(newTimePeriod);
      if (timePeriod !== newTimePeriod) {
        setDisabled(false);
      }
    },
  });

  const [saveProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onError({ ...err }) {
      console.log(err);
      setDisabled(false);
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
    onCompleted(data) {
      const newTimePeriod = Number.parseInt(data.updateProperty.value);
      setTimePeriod(newTimePeriod);
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
  });

  const handleCancel = () => {
    setDisabled(true);
    setTimePeriod(Number.parseInt(data));
  };

  const handleSave = () => {
    setDisabled(true);
    saveProperty({
      variables: {
        resource: "myTime",
        key: "timePeriod",
        value: timePeriod.toString(),
      },
    });
    setShowConfirmDialog(false);
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleTimePeriodChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedValue = event.target.value;
    const currentDataValue = data?.toString();
    if (selectedValue !== currentDataValue) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }

    setTimePeriod(Number.parseInt(selectedValue));
  };

  useEffect(() => {
    if (data && timePeriod !== undefined) {
      if (timePeriod.toString() === data.toString()) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    }
  }, [data, timePeriod]);

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_TIME_TIME_PERIOD_SETTINGS")}>
        <Box
          padding="8px 0px 24px 0px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
        >
          <RadioGroup value={timePeriod} onChange={handleTimePeriodChange}>
            <MyTimeTimePeriodOption
              id="no-time-period"
              value={0}
              label={t("MY_TIME_NO_TIME_PERIOD")}
              infoLabel={t("MY_TIME_NO_TIME_PERIOD_INFO")}
              isMobile={isMobile}
            />
            <MyTimeTimePeriodOption
              id="10-minutes-time-period"
              value={10}
              label={t("MY_TIME_10_MINUTES_TIME_PERIOD")}
              infoLabel={t("MY_TIME_10_MINUTES_TIME_PERIOD_INFO")}
              isMobile={isMobile}
            />
            <MyTimeTimePeriodOption
              id="30-minutes-time-period"
              value={30}
              label={t("MY_TIME_30_MINUTES_TIME_PERIOD")}
              infoLabel={t("MY_TIME_30_MINUTES_TIME_PERIOD_INFO")}
              isMobile={isMobile}
            />
          </RadioGroup>
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          handlePrimaryButton={() => setShowConfirmDialog(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={disabled}
        />
      </OcCard>
    </>
  );
};

export default MyTimeTimePeriodSettingsCard;
